import React from 'react';
import { Badge, Button } from 'react-bootstrap';
import { SurveysAPI } from '../../../API';
import { DashboardDetails } from '../DashboardDetails';
import { isPanelCountryByName } from '../surveyConstants';
import { KpiTiles } from './KpiTiles';
import { StatusSelect } from './StatusSelect';
import {
  Kpis, InFieldRow, kpisFromInFieldStats, statsBySurvey, marketLabels, weightedLoi,
} from './surveyMetrics';

type Props = {
  survey: any;
  // The markets to list: a parent's children, or a child page's own survey.
  markets: any[];
  // In-field rows are fetched once by SurveyDetailPage and shared with the Markets list.
  inFieldRows: InFieldRow[];
  inFieldLoaded: boolean;
  role: string;
  // A status picked on a market row; SurveyDetailPage runs the guards and the confirmation.
  onStatusSelect: (market: any, next: string) => void;
  // Navigate to another detail tab.
  onNavigate: (tab: 'setup' | 'activity') => void;
};

type State = { medianLoi: number | null };

// getAll (paginated branch) returns parents and their children as separate flat
// rows (children are fetched by parentid). Pick the rows that belong to this survey.
export const loiRowsFor = (survey: any, allRows: any[]): any[] => {
  if (!Array.isArray(allRows)) return [];
  if (survey.parentid) return allRows.filter((r) => String(r.id) === String(survey.id));
  return allRows.filter((r) => String(r.id) === String(survey.id) || String(r.parentid) === String(survey.id));
};

export class OverviewPane extends React.Component<Props, State> {
  private _mounted = false;

  state: State = { medianLoi: null };

  componentDidMount() {
    this._mounted = true;
    this.load();
  }

  componentWillUnmount() {
    this._mounted = false;
  }

  load = async () => {
    const { survey } = this.props;
    const listRes = await SurveysAPI.getAll(10, 1, { uniqueid: survey.uniqueid }).catch(() => null);
    if (!this._mounted) return;
    const listRows: any[] = Array.isArray(listRes) ? listRes : (listRes as any)?.data || [];
    this.setState({
      medianLoi: weightedLoi(loiRowsFor(survey, listRows)),
    });
  };

  render() {
    const { survey, markets, onNavigate, inFieldRows: rows, role, onStatusSelect } = this.props;
    const { medianLoi } = this.state;
    const kpis: Kpis = kpisFromInFieldStats(rows, Number(survey.userLimitCommitted) || 0);
    const bySurvey = statsBySurvey(rows);
    const labels = marketLabels(markets);

    return (
      <div className="row">
        <div className="col-lg-8">
          <div className="dx-card mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="mb-0">Survey details</h5>
              <Button variant="link" size="sm" onClick={() => onNavigate('setup')}>Edit setup</Button>
            </div>
            <DashboardDetails id={survey.id} inline sections={['info']} />
          </div>

          <div className="dx-card mb-3">
            <h5>Markets</h5>
            <div className="table-responsive">
              <table className="table table-sm dx-table mb-0">
                <thead>
                  <tr>
                    <th>Market</th><th>Status</th><th>Quota</th><th>Completes</th><th>Hits</th><th>IR %</th>
                  </tr>
                </thead>
                <tbody>
                  {markets.length === 0 && (
                    <tr><td colSpan={6} className="text-muted">No markets yet.</td></tr>
                  )}
                  {markets.map((c) => {
                    const s = bySurvey[c.id];
                    return (
                      <tr key={c.id}>
                        <td>
                          <strong>{labels[c.id]}</strong> {c.country}{' '}
                          {isPanelCountryByName(c.country) && <Badge variant="info">panel</Badge>}
                        </td>
                        <td>
                          <StatusSelect
                            role={role}
                            value={c.surveyType}
                            ariaLabel={`Status of ${labels[c.id]}`}
                            onSelect={(next) => onStatusSelect(c, next)}
                          />
                        </td>
                        <td>{c.userLimitCommitted ?? '-'}</td>
                        <td>{s ? s.completes : '-'}</td>
                        <td>{s ? s.hits : '-'}</td>
                        <td>{s ? s.irPct.toFixed(2) : '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <details className="dx-card mb-3">
            <summary className="font-weight-bold">Client redirect links</summary>
            <div className="mt-2">
              <DashboardDetails id={survey.id} inline sections={['links']} />
            </div>
          </details>
        </div>

        <div className="col-lg-4">
          <KpiTiles kpis={kpis} medianLoi={medianLoi} publishDate={survey.publishDate} expiryDate={survey.expiryDate} />
          <div className="dx-card mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="mb-0">Recent activity</h5>
              <Button variant="link" size="sm" onClick={() => onNavigate('activity')}>View all</Button>
            </div>
            <div className="dx-recent">
              <DashboardDetails id={survey.id} inline sections={['changelog']} />
            </div>
          </div>
        </div>
      </div>
    );
  }
}
